import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { SocietyFeaturesProvider } from "./context/SocietyFeaturesContext";
import ProtectedRoute from "./router/ProtectedRoute";

import Landing from "./pages/Landing";
import ContactPage from "./pages/ContactPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import LoginPage from "./pages/LoginPage";
import Register from "./pages/Register";
import ResidentRegister from "./pages/ResidentRegister";
import RequestAccessPage from "./pages/RequestAccessPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import SetupCommunityPage from "./pages/SetupCommunityPage";
import NotFoundPage from "./pages/NotFoundPage";
import AdminPage from "./pages/admin/AdminPage";
import Dashboard from "./pages/Dashboard";

import ThemeExecutiveSuite from "./pages/themes/ThemeExecutiveSuite";
import ThemeModernMinimal from "./pages/themes/ThemeModernMinimal";
import ThemeCorporateEdge from "./pages/themes/ThemeCorporateEdge";

import ResidentsPage from "./pages/sections/ResidentsPage";
import MoveRequestsPage from "./pages/sections/MoveRequestsPage";
import ParkingPage from "./pages/sections/ParkingPage";
import PollsPage from "./pages/sections/PollsPage";
import HelpersPage from "./pages/sections/HelpersPage";
import BlacklistPage from "./pages/sections/BlacklistPage";
import ExpensesPage from "./pages/sections/ExpensesPage";
import LedgerPage from "./pages/sections/LedgerPage";
import BudgetsPage from "./pages/sections/BudgetsPage";
import FundsPage from "./pages/sections/FundsPage";
import BlocksPage from "./pages/sections/BlocksPage";
import UnitTypesPage from "./pages/sections/UnitTypesPage";
import UnitsPage from "./pages/sections/UnitsPage";
import VisitorsPage from "./pages/sections/VisitorsPage";
import DeliveriesPage from "./pages/sections/DeliveriesPage";
import FacilitiesPage from "./pages/sections/FacilitiesPage";
import FacilityBookingPage from "./pages/sections/FacilityBookingPage";
import BillingPage from "./pages/sections/BillingPage";
import MyBillingPage from "./pages/sections/MyBillingPage";
import HelpdeskPage from "./pages/sections/HelpdeskPage";
import AssetMaintenancePage from "./pages/sections/AssetMaintenancePage";
import StaffPage from "./pages/sections/StaffPage";
import MaintenancePage from "./pages/sections/MaintenancePage";
import NoticesPage from "./pages/sections/NoticesPage";
import EventsPage from "./pages/sections/EventsPage";
import SecurityPage from "./pages/sections/SecurityPage";
import DocumentsPage from "./pages/sections/DocumentsPage";
import CommunityFeaturesPage from "./pages/sections/CommunityFeaturesPage";
import CommunityFeedPage from "./pages/sections/CommunityFeedPage";
import TeamMembersPage from "./pages/sections/TeamMembersPage";
import MonthlyChargesPage from "./pages/sections/MonthlyChargesPage";
import PaymentConfigPage from "./pages/sections/PaymentConfigPage";
import SettingsPage from "./pages/sections/SettingsPage";
import HelpPage from "./pages/sections/HelpPage";
import DocsPage from "./pages/sections/DocsPage";
import NexaBotPage from "./pages/sections/NexaBotPage";
import AIInsightsPage from "./pages/sections/AIInsightsPage";
import AnalyticsExecutivePage from "./pages/sections/AnalyticsExecutivePage";
import ReportsPage from "./pages/sections/ReportsPage";
import AuditLogsPage from "./pages/sections/AuditLogsPage";
import ChatPage from "./pages/sections/ChatPage";
import AdminMessagesPage from "./pages/sections/AdminMessagesPage";
import CommitteeMeetingsPage from "./pages/sections/CommitteeMeetingsPage";
import MarketplacePage from "./pages/sections/MarketplacePage";
import ViolationsPage from "./pages/sections/ViolationsPage";
import VehiclesPage from "./pages/sections/VehiclesPage";
import DepositsPage from "./pages/sections/DepositsPage";

import "./styles/nexora.css";

/* ── PublicRoute ─────────────────────────────────────────────────────────── */

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  if (isLoading) return null;
  if (isAuthenticated) {
    return (
      <Navigate
        to={user?.role === "SUPER_ADMIN" ? "/admin" : "/dashboard"}
        replace
      />
    );
  }
  return <>{children}</>;
};

/* ── SetupGuard ──────────────────────────────────────────────────────────── */

const SetupGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  if (user?.societyId) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

/* ── App ─────────────────────────────────────────────────────────────────── */

const AppRoutes: React.FC = () => (
  <Routes>
    {/* Landing */}
    <Route path="/" element={<Landing />} />
    <Route path="/contact" element={<ContactPage />} />
    <Route path="/privacy" element={<PrivacyPage />} />
    <Route path="/terms" element={<TermsPage />} />

    {/* Public (redirect to /dashboard if already authed) */}
    <Route
      path="/login"
      element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      }
    />
    <Route
      path="/register"
      element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      }
    />
    <Route
      path="/resident-register"
      element={
        <PublicRoute>
          <ResidentRegister />
        </PublicRoute>
      }
    />
    <Route
      path="/request-access"
      element={
        <PublicRoute>
          <RequestAccessPage />
        </PublicRoute>
      }
    />
    <Route
      path="/forgot-password"
      element={
        <PublicRoute>
          <ForgotPasswordPage />
        </PublicRoute>
      }
    />
    <Route
      path="/reset-password"
      element={
        <PublicRoute>
          <ResetPasswordPage />
        </PublicRoute>
      }
    />

    {/* Society setup — SOCIETY_ADMIN only, only before society is active */}
    <Route
      path="/setup-community"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN"]}>
          <SetupGuard>
            <SetupCommunityPage />
          </SetupGuard>
        </ProtectedRoute>
      }
    />

    {/* Super-admin panel */}
    <Route
      path="/admin/*"
      element={
        <ProtectedRoute allowedRoles={["SUPER_ADMIN"]}>
          <AdminPage />
        </ProtectedRoute>
      }
    />

    {/* Dashboard — society staff */}
    <Route
      path="/dashboard/themes/executive-suite"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF"]}>
          <ThemeExecutiveSuite />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/themes/modern-minimal"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF"]}>
          <ThemeModernMinimal />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/themes/corporate-edge"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF"]}>
          <ThemeCorporateEdge />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/blocks"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF"]}>
          <BlocksPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/units"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF"]}>
          <UnitsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/unit-types"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF"]}>
          <UnitTypesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/residents"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF", "RESIDENT", "TENANT"]}>
          <ResidentsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/move-requests"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF", "RESIDENT", "TENANT"]}>
          <MoveRequestsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/parking"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF", "RESIDENT", "TENANT"]}>
          <ParkingPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/helpers"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF"]}>
          <HelpersPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/blacklist"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF"]}>
          <BlacklistPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/visitors"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF", "RESIDENT", "TENANT"]}>
          <VisitorsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/deliveries"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF", "RESIDENT", "TENANT"]}>
          <DeliveriesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/facilities"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF"]}>
          <FacilitiesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/facility-booking"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF", "RESIDENT", "TENANT"]}>
          <FacilityBookingPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/billing"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF"]}>
          <BillingPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/my-billing"
      element={
        <ProtectedRoute allowedRoles={["RESIDENT", "TENANT"]}>
          <MyBillingPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/expenses"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF"]}>
          <ExpensesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/ledger"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF"]}>
          <LedgerPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/budgets"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF"]}>
          <BudgetsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/funds"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF"]}>
          <FundsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/helpdesk"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF", "RESIDENT", "TENANT"]}>
          <HelpdeskPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/maintenance"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF"]}>
          <MaintenancePage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/assets"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF"]}>
          <AssetMaintenancePage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/staff"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF"]}>
          <StaffPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/analytics"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "SUPER_ADMIN"]}>
          <AnalyticsExecutivePage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/reports"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "SUPER_ADMIN"]}>
          <ReportsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/audit-logs"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "SUPER_ADMIN"]}>
          <AuditLogsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/messages"
      element={
        <ProtectedRoute allowedRoles={["RESIDENT", "TENANT"]}>
          <ChatPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/admin-messages"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "SUPER_ADMIN"]}>
          <AdminMessagesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/committee-meetings"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF", "RESIDENT", "TENANT"]}>
          <CommitteeMeetingsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/marketplace"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF", "RESIDENT", "TENANT"]}>
          <MarketplacePage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/violations"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF", "RESIDENT", "TENANT"]}>
          <ViolationsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/vehicles"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF", "RESIDENT", "TENANT"]}>
          <VehiclesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/deposits"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF"]}>
          <DepositsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/notices"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF", "RESIDENT", "TENANT"]}>
          <NoticesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/polls"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF", "RESIDENT", "TENANT"]}>
          <PollsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/events"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF", "RESIDENT", "TENANT"]}>
          <EventsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/security"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF", "RESIDENT", "TENANT"]}>
          <SecurityPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/documents"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF", "TREASURER", "RESIDENT", "TENANT"]}>
          <DocumentsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/community-features"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF"]}>
          <CommunityFeaturesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/community-feed"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF", "RESIDENT", "TENANT"]}>
          <CommunityFeedPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/team-members"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN"]}>
          <TeamMembersPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/monthly-charges"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF"]}>
          <MonthlyChargesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/payment-config"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN"]}>
          <PaymentConfigPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/nexabot"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF"]}>
          <NexaBotPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/insights"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF"]}>
          <AIInsightsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/settings"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF", "RESIDENT", "TENANT"]}>
          <SettingsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/help"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF", "RESIDENT", "TENANT"]}>
          <HelpPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/docs"
      element={
        <ProtectedRoute>
          <DocsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF", "RESIDENT", "TENANT"]}>
          <Dashboard />
        </ProtectedRoute>
      }
    />
    <Route
      path="/dashboard/*"
      element={
        <ProtectedRoute allowedRoles={["SOCIETY_ADMIN", "STAFF", "RESIDENT", "TENANT"]}>
          <Dashboard />
        </ProtectedRoute>
      }
    />

    {/* Catch-all */}
    <Route path="*" element={<NotFoundPage />} />
  </Routes>
);

const App: React.FC = () => (
  <BrowserRouter
    future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
  >
    <AuthProvider>
      <SocietyFeaturesProvider>
        <ThemeProvider>
          <AppRoutes />
        </ThemeProvider>
      </SocietyFeaturesProvider>
    </AuthProvider>
  </BrowserRouter>
);

export default App;
